import React, { useState, useEffect } from "react";
import {
  getCompanyDetails,
  getAllUsers,
  getAllProjects,
  addUserToCompany,
  removeUserFromCompany,
  addProjectToCompany,
  removeProjectFromCompany,
  batchAddUsersToCompany,
  batchAddProjectsToCompany,
  createProject,
  uploadProjectLogo,
  updateMemberRole,
  listOrgRoles,
  listUserAccess,
  scopeUserToClients,
  unscopeUser,
} from "../api";
import { useNotification } from "../contexts/NotificationContext";
import { supabase } from "../lib/supabase";

// Friendly labels for role names. The backend stores snake_case identifiers
// (org_viewer, org_admin…) that mean nothing to a customer. Map them to plain
// English so the dropdown + badge read naturally. Unknown roles fall back to
// a title-cased version of the raw name.
const ROLE_LABELS = {
  platform_admin: "Platform Admin",
  org_owner: "Owner",
  org_admin: "Admin",
  org_analyst: "Analyst",
  org_viewer: "Viewer",
  org_scoped: "Scoped",
};
const roleLabel = (name) => {
  if (!name) return "—";
  if (ROLE_LABELS[name]) return ROLE_LABELS[name];
  // title-case the snake_case name as a fallback
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const CompanyDetailModal = ({ open, onClose, companyId, onUpdate }) => {
  const { showSuccess, showError, showConfirm } = useNotification();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  
  // For adding users/projects
  const [showAddUserPanel, setShowAddUserPanel] = useState(false);
  const [showAddProjectPanel, setShowAddProjectPanel] = useState(false);
  const [showCreateProjectForm, setShowCreateProjectForm] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [addingUsers, setAddingUsers] = useState(false);
  const [addingProjects, setAddingProjects] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [searchProject, setSearchProject] = useState("");
  // Search fields for the CURRENT-list tabs (separate from the add-panel
  // searches above, which filter the "available users/clients to add" pools).
  const [listUserQuery, setListUserQuery] = useState("");
  const [listProjectQuery, setListProjectQuery] = useState("");

  // 2026-08-08 — members + roles for the Users-tab role dropdown. Members come
  // from fn_list_org_members (the API company payload carries no role_id);
  // roles come from the `roles` table (platform_admin excluded — internal-only,
  // same rule as AddEditUserModal).
  const [members, setMembers] = useState([]);
  const [orgRoles, setOrgRoles] = useState([]);
  const [memberRoleBusy, setMemberRoleBusy] = useState({}); // { [userId]: bool }
  const [memberRoleMsg, setMemberRoleMsg] = useState("");
  const currentUserRole = Number(localStorage.getItem("user_role"));
  const canEditRoles = currentUserRole === 0 || currentUserRole === 1; // platform_admin or org owner/admin

  // New project form data
  const [projectFormData, setProjectFormData] = useState({
    name: "",
    description: "",
    type: "chat",
    vf_project_id: "",
    vf_version_id: "",
    vf_api_key: "",
    group_name: "",
    ai_enabled: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectError, setProjectError] = useState("");

  useEffect(() => {
    if (open && companyId) {
      fetchCompanyDetails();
    }
  }, [open, companyId]);

  // Load org members (role_id mapping) + org roles when the Users tab shows.
  useEffect(() => {
    let cancelled = false;
    if (!open || activeTab !== "users" || !companyId) return undefined;
    (async () => {
      try {
        const { data, error } = await supabase.rpc("fn_list_org_members", { p_org_id: companyId });
        if (!cancelled && !error) setMembers((data || []).filter((m) => !m.removed_at));
      } catch { /* non-fatal */ }
      try {
        const r = await listOrgRoles();
        const list = r?.data ?? r ?? [];
        if (!cancelled) setOrgRoles((Array.isArray(list) ? list : []).filter((x) => x.name !== "platform_admin"));
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [open, activeTab, companyId]);

  // ─── Epic 3 (2026-08-13) — per-member property reach ───────────────────────
  // For each member, show which properties they reach: all of them (any non-
  // scoped role resolves every org client through the org), or a named subset
  // (org_scoped — access comes from per-client resource_grants, not the org
  // role). Only org_scoped members need a fetch; the rest are "All (N)" for
  // free from company.projects.length.
  //
  // memberReach: { [userId]: { loading: bool, names: string[]|null, error: bool } }
  //   names === null  -> not scoped -> "All properties" (caller renders the count)
  //   names === []    -> scoped but no grants resolved (edge: "0 properties")
  //   names == ['..'] -> scoped subset
  const [memberReach, setMemberReach] = useState({});

  // Normalize fn_admin_list_user_access output (shape not documented in repo)
  // to { ids, names } for the client grants this user holds in THIS org.
  // Cross-reference grant resource_ids against company.projects so names resolve
  // even if the RPC returns only ids. Defensive across array/object shapes.
  // Epic 3 uses .names for the reach line; Epic 4 uses .ids to seed the
  // scope multi-select and to know which grants to revoke on "restore all".
  const grantsToClients = (accessData, orgClients) => {
    const byId = new Map((orgClients || []).map((c) => [String(c.id), c.name || c.id]));
    const ids = new Set();
    const push = (r) => {
      if (!r || typeof r !== 'object') return;
      const rt = r.resource_type || r.type || r.scope || r.resource;
      const rid = r.resource_id || r.client_id || r.id || r.scope_id || r.resourceId;
      if (rt && String(rt).toLowerCase() !== 'client') return;
      if (rid) ids.add(String(rid));
    };
    let rows = accessData;
    if (accessData && typeof accessData === 'object' && !Array.isArray(accessData)) {
      rows = accessData.grants || accessData.client_grants || accessData.direct
        || accessData.access || accessData.rows || [];
    }
    if (Array.isArray(rows)) rows.forEach(push);
    const names = [];
    for (const id of ids) {
      const name = byId.get(id);
      if (name) names.push(name);
    }
    return { ids: Array.from(ids), names: names.sort((a, b) => a.localeCompare(b)) };
  };

  useEffect(() => {
    let cancelled = false;
    if (!open || activeTab !== "users" || !companyId) return undefined;
    const orgClients = company?.projects || [];
    const scoped = members.filter((m) => m.role_name === "org_scoped");
    if (scoped.length === 0) return undefined;
    (async () => {
      for (const m of scoped) {
        const uid = m.user_id;
        if (!uid) continue;
        setMemberReach((prev) => (prev[uid] ? prev : { ...prev, [uid]: { loading: true, names: null, ids: [], error: false } }));
        try {
          // Primary: query resource_grants directly. We know the schema from the
          // spec (subject_type/subject_id/resource_type/resource_id), which is
          // more reliable than guessing fn_admin_list_user_access's return shape.
          let grantIds = [];
          const { data: rgRows, error: rgErr } = await supabase
            .from('resource_grants')
            .select('resource_type, resource_id')
            .eq('subject_type', 'user')
            .eq('subject_id', uid)
            .eq('resource_type', 'client');
          if (rgErr) {
            // RLS may block the direct read; fall back to the RPC.
            console.warn('[Epic3] resource_grants direct read failed, falling back to fn_admin_list_user_access:', rgErr.message);
          } else if (Array.isArray(rgRows)) {
            grantIds = rgRows.map((r) => r?.resource_id).filter(Boolean).map(String);
          }
          // If the direct query returned nothing (or was blocked), try the RPC.
          if (grantIds.length === 0) {
            try {
              const res = await listUserAccess(uid);
              const data = res?.data ?? res;
              const normalized = grantsToClients(data, orgClients);
              grantIds = normalized.ids;
              if (grantIds.length === 0 && data) {
                console.log('[Epic3] fn_admin_list_user_access returned no parseable client grants. Raw shape:', JSON.stringify(data).slice(0, 500));
              }
            } catch (rpcErr) {
              console.warn('[Epic3] fn_admin_list_user_access also failed:', rpcErr?.message || rpcErr);
            }
          }
          if (cancelled) return;
          const byId = new Map(orgClients.map((c) => [String(c.id), c.name || c.id]));
          const names = grantIds.map((id) => byId.get(id)).filter(Boolean).sort((a, b) => a.localeCompare(b));
          setMemberReach((prev) => ({ ...prev, [uid]: { loading: false, names, ids: grantIds, error: false } }));
        } catch (e) {
          if (cancelled) return;
          setMemberReach((prev) => ({ ...prev, [uid]: { loading: false, names: null, ids: [], error: true } }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, activeTab, companyId, members, company]);

  // ─── Epic 4 (2026-08-13) — scope-a-member sub-modal ────────────────────────
  // A multi-select of the org's clients. Seeded from the member's current reach:
  //   - non-scoped (any role): all clients checked (they reach everything)
  //   - org_scoped: only the granted clients checked (from memberReach[uid].ids)
  // Saving with a partial selection -> scopeUserToClients (idempotent re-run).
  // Saving with ALL selected -> unscopeUser (restore org_viewer + revoke grants),
  // per the spec: "Selecting all of them should mean 'not scoped'".
  const [scopeTarget, setScopeTarget] = useState(null); // { user, memberRow }
  const [scopeSelection, setScopeSelection] = useState({}); // { [clientId]: true }
  const [scopeBusy, setScopeBusy] = useState(false);
  const [scopeError, setScopeError] = useState("");

  const openScopeModal = (user, memberRow) => {
    const orgClients = company?.projects || [];
    const reach = memberRow?.role_name === "org_scoped" ? memberReach[user.id] : null;
    // Seeding logic:
    //   non-scoped       -> all checked (they reach everything)
    //   scoped, loading  -> all checked (fallback until reach loads, so UI isn't empty)
    //   scoped, loaded   -> ONLY the granted ids checked (even if empty = 0 grants)
    let checked;
    if (!reach || reach.loading) {
      checked = new Set(orgClients.map((c) => String(c.id)));
    } else {
      checked = new Set((reach.ids || []).map(String));
    }
    const sel = {};
    for (const c of orgClients) sel[String(c.id)] = checked.has(String(c.id));
    setScopeError("");
    setScopeSelection(sel);
    setScopeTarget({ user, memberRow });
  };

  const closeScopeModal = () => {
    if (scopeBusy) return;
    setScopeTarget(null);
    setScopeSelection({});
    setScopeError("");
  };

  const handleScopeSave = async () => {
    if (!scopeTarget || !companyId) return;
    const { user, memberRow } = scopeTarget;
    const orgClients = company?.projects || [];
    const allIds = orgClients.map((c) => String(c.id));
    const selected = allIds.filter((id) => scopeSelection[id]);
    const isAll = selected.length === allIds.length && allIds.length > 0;
    setScopeError("");
    const action = isAll ? "restore full access (un-scope)" : `scope to ${selected.length} ${selected.length === 1 ? "property" : "properties"}`;
    const confirmed = await showConfirm({
      title: "Confirm property scoping",
      message: `Apply ${action} for ${user.first_name || user.display_name || user.email}? This changes what they can see on next login.`,
      confirmText: "Apply",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    setScopeBusy(true);
    try {
      if (isAll) {
        const reach = memberReach[user.id];
        await unscopeUser(user.id, companyId, reach?.ids || []);
      } else {
        await scopeUserToClients(user.id, companyId, selected);
      }
      // Refresh members so the role_name + reach update immediately.
      try {
        const { data: mdata } = await supabase.rpc("fn_list_org_members", { p_org_id: companyId });
        setMembers((mdata || []).filter((m) => !m.removed_at));
      } catch { /* reach summary will self-correct on next render */ }
      setMemberReach((prev) => {
        const next = { ...prev };
        delete next[user.id]; // force re-fetch if still scoped
        return next;
      });
      showSuccess("Scoping applied", `Updated property access for ${user.first_name || user.display_name || user.email}.`);
      setScopeTarget(null);
      setScopeSelection({});
      if (typeof onUpdate === "function") onUpdate();
    } catch (e) {
      setScopeError(e?.message || "Failed to apply scoping");
    } finally {
      setScopeBusy(false);
    }
  };




  const handleMemberRoleChange = async (userId, roleId) => {
    const prev = members;
    setMemberRoleMsg("");
    setMemberRoleBusy((b) => ({ ...b, [userId]: true }));
    setMembers((ms) => ms.map((m) => (m.user_id === userId
      ? { ...m, role_id: roleId, role_name: orgRoles.find((r) => r.id === roleId)?.name || m.role_name }
      : m)));
    try {
      await updateMemberRole(companyId, userId, roleId);
      showSuccess("Role updated", "The member's role has been updated.");
    } catch (e) {
      setMembers(prev); // rollback on failure
      setMemberRoleMsg(e?.response?.data?.message || e?.message || "Failed to update role.");
    } finally {
      setMemberRoleBusy((b) => ({ ...b, [userId]: false }));
    }
  };

  // Reset form when opening create project
  useEffect(() => {
    if (showCreateProjectForm) {
      setProjectFormData({
        name: "",
        description: "",
        type: "chat",
        vf_project_id: "",
        vf_version_id: "",
        vf_api_key: "",
        group_name: "",
        ai_enabled: false,
      });
      setLogoFile(null);
      setLogoPreview(null);
      setProjectError("");
    }
  }, [showCreateProjectForm]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await getCompanyDetails(companyId);
      setCompany(response?.data || response);
    } catch (err) {
      showError("Error", err?.message || "Failed to load company details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await getAllUsers();
      const users = response?.data || response || [];
      // Filter out users already in this company
      const companyUserIds = (company?.users || []).map(u => u.id);
      const available = users.filter(u => !companyUserIds.includes(u.id) && u.role === 3); // Only clients
      setAvailableUsers(available);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  const fetchAvailableProjects = async () => {
    try {
      // Pass true to get ALL projects (admin mode) bypassing company filters
      const response = await getAllProjects(true);
      console.log("getAllProjects response:", response);
      const projects = response?.data || response || [];
      console.log("Parsed projects:", projects);
      // Filter out projects already in this company (allow reassigning from other companies)
      const companyProjectIds = (company?.projects || []).map(p => p.id);
      const available = projects.filter(p => !companyProjectIds.includes(p.id));
      console.log("Available projects (not in company):", available);
      setAvailableProjects(available);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      showError("Error", err?.message || "Failed to load projects");
    }
  };

  const handleShowAddUser = () => {
    setShowAddUserPanel(true);
    fetchAvailableUsers();
  };

  const handleShowAddProject = () => {
    setShowAddProjectPanel(true);
    fetchAvailableProjects();
  };

  const handleAddSelectedUsers = async () => {
    if (selectedUsers.length === 0) return;
    
    try {
      setAddingUsers(true);
      await batchAddUsersToCompany(companyId, selectedUsers);
      showSuccess("Users Added", `${selectedUsers.length} user(s) added to ${company.name}`);
      setSelectedUsers([]);
      setShowAddUserPanel(false);
      fetchCompanyDetails();
      onUpdate?.();
    } catch (err) {
      showError("Error", err?.message || "Failed to add users");
    } finally {
      setAddingUsers(false);
    }
  };

  const handleAddSelectedProjects = async () => {
    if (selectedProjects.length === 0) return;
    
    try {
      setAddingProjects(true);
      await batchAddProjectsToCompany(companyId, selectedProjects);
      showSuccess("Clients Added", `${selectedProjects.length} client(s) added to ${company.name}`);
      setSelectedProjects([]);
      setShowAddProjectPanel(false);
      fetchCompanyDetails();
      onUpdate?.();
    } catch (err) {
      showError("Error", err?.message || "Failed to add projects");
    } finally {
      setAddingProjects(false);
    }
  };

  const handleRemoveUser = async (user) => {
    const confirmed = await showConfirm({
      title: 'Remove User',
      message: `Remove ${user.first_name || user.display_name || user.email} from ${company.name}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-500 hover:bg-red-600',
    });

    if (!confirmed) return;

    try {
      await removeUserFromCompany(companyId, user.id);
      showSuccess("User Removed", `${user.first_name || user.display_name || user.email} removed from company`);
      fetchCompanyDetails();
      onUpdate?.();
    } catch (err) {
      showError("Error", err?.message || "Failed to remove user");
    }
  };

  const handleRemoveProject = async (project) => {
    const confirmed = await showConfirm({
      title: 'Remove Project',
      message: `Remove ${project.name} from ${company.name}?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      confirmButtonClass: 'bg-red-500 hover:bg-red-600',
    });

    if (!confirmed) return;

    try {
      await removeProjectFromCompany(companyId, project.id);
      showSuccess("Project Removed", `${project.name} removed from company`);
      fetchCompanyDetails();
      onUpdate?.();
    } catch (err) {
      showError("Error", err?.message || "Failed to remove project");
    }
  };

  // Handle project form changes
  const handleProjectFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProjectFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setProjectError("Logo file size must be less than 5MB");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // Create new project
  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreatingProject(true);
    setProjectError("");

    try {
      const projectData = {
        name: projectFormData.name,
        description: projectFormData.description,
        type: projectFormData.type,
        vf_project_id: projectFormData.vf_project_id,
        vf_version_id: projectFormData.vf_version_id,
        vf_api_key: projectFormData.vf_api_key,
        group_name: projectFormData.group_name,
        ai_enabled: projectFormData.ai_enabled,
        company_id: companyId, // Assign to this company directly
      };

      const response = await createProject(projectData);
      const projectId = response?.data?.id || response?.id;

      // Upload logo if selected
      if (logoFile && projectId) {
        await uploadProjectLogo(projectId, logoFile);
      }

      showSuccess("Client Created", `${projectFormData.name} has been created and added to ${company.name}`);
      setShowCreateProjectForm(false);
      fetchCompanyDetails();
      onUpdate?.();
    } catch (err) {
      setProjectError(err?.message || "Failed to create project");
    } finally {
      setCreatingProject(false);
    }
  };

  const filteredAvailableUsers = availableUsers.filter(user => {
    const search = searchUser.toLowerCase();
    return (
      (user.display_name || '').toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search)
    );
  });

  const filteredAvailableProjects = availableProjects.filter(project => {
    const search = searchProject.toLowerCase();
    return project.name?.toLowerCase().includes(search);
  });

  if (!open) return null;

  // Create Project Form
  if (showCreateProjectForm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  Create New Client
                </h2>
                <p className="text-sm text-gray-500">for {company?.name}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleCreateProject} className="p-6 space-y-4">
            {projectError && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-300 text-sm">
                {projectError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Project Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={projectFormData.name}
                  onChange={handleProjectFormChange}
                  required
                  placeholder="e.g., Main Chat Bot"
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={projectFormData.description}
                  onChange={handleProjectFormChange}
                  rows="2"
                  placeholder="Brief description of this project..."
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Type *
                </label>
                <select
                  name="type"
                  value={projectFormData.type}
                  onChange={handleProjectFormChange}
                  required
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="chat">Chat</option>
                  <option value="voice">Voice</option>
                </select>
              </div>

              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Group Name
                </label>
                <input
                  type="text"
                  name="group_name"
                  value={projectFormData.group_name}
                  onChange={handleProjectFormChange}
                  placeholder="Optional group"
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Voiceflow Project ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Voiceflow Project ID *
                </label>
                <input
                  type="text"
                  name="vf_project_id"
                  value={projectFormData.vf_project_id}
                  onChange={handleProjectFormChange}
                  required
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Voiceflow Version ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Voiceflow Version ID *
                </label>
                <input
                  type="text"
                  name="vf_version_id"
                  value={projectFormData.vf_version_id}
                  onChange={handleProjectFormChange}
                  required
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Voiceflow API Key */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Voiceflow API Key *
                </label>
                <input
                  type="password"
                  name="vf_api_key"
                  value={projectFormData.vf_api_key}
                  onChange={handleProjectFormChange}
                  required
                  className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Logo Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Project Logo
                </label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded-lg object-cover border dark:border-gray-700" />
                      <button
                        type="button"
                        onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                      >×</button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <label className="cursor-pointer px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">
                    Choose File
                    <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                  </label>
                </div>
              </div>

              {/* AI Toggle */}
              <div className="md:col-span-2">
                <div className="flex items-center justify-between p-4 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 7H7v6h6V7z" />
                        <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">AI Capabilities</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Enable AI tagging and summaries</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="ai_enabled"
                      checked={projectFormData.ai_enabled}
                      onChange={handleProjectFormChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 dark:peer-focus:ring-emerald-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreateProjectForm(false)}
                className="flex-1 px-4 py-2.5 border dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creatingProject}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg disabled:opacity-50 transition-all font-medium"
              >
                {creatingProject ? "Creating..." : "Create Client"}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b dark:border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {company?.logoUrl || company?.logo ? (
                <img 
                  src={company.logoUrl || company.logo} 
                  alt={company.name} 
                  className="w-14 h-14 rounded-xl object-cover border dark:border-gray-700"
                />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {loading ? "Loading..." : company?.name}
                </h2>
                {company?.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{company.description}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Stats */}
          {!loading && company && (
            <div className="flex gap-4 mt-4">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-4 h-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  {company.users?.length || 0} Users
                </span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 rounded-lg">
                <svg className="w-4 h-4 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                </svg>
                <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                  {company.projects?.length || 0} Clients
                </span>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                company.status === 1 
                  ? "bg-green-50 dark:bg-green-900/30" 
                  : "bg-red-50 dark:bg-red-900/30"
              }`}>
                <span className={`w-2 h-2 rounded-full ${
                  company.status === 1 ? "bg-green-500" : "bg-red-500"
                }`}></span>
                <span className={`text-sm font-medium ${
                  company.status === 1 
                    ? "text-green-700 dark:text-green-300" 
                    : "text-red-700 dark:text-red-300"
                }`}>
                  {company.status === 1 ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b dark:border-gray-800">
          <div className="flex gap-1 px-6">
            <button
              onClick={() => setActiveTab("users")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "users"
                  ? "border-emerald-500 text-emerald-600 dark:text-emerald-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                Users ({company?.users?.length || 0})
              </span>
            </button>
            <button
              onClick={() => setActiveTab("projects")}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "projects"
                  ? "border-emerald-500 text-emerald-600 dark:text-emerald-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                </svg>
                Clients ({company?.projects?.length || 0})
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : (
            <>
              {/* Users Tab */}
              {activeTab === "users" && (
                <div className="space-y-4">
                  {/* Add User Button or Panel */}
                  {showAddUserPanel ? (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add Users to Organization</h3>
                        <button
                          onClick={() => {
                            setShowAddUserPanel(false);
                            setSelectedUsers([]);
                            setSearchUser("");
                          }}
                          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        className="w-full px-3 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm"
                      />

                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {filteredAvailableUsers.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No available users to add</p>
                        ) : (
                          filteredAvailableUsers.map(user => (
                            <label
                              key={user.id}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                selectedUsers.includes(user.id)
                                  ? "bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedUsers.includes(user.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsers([...selectedUsers, user.id]);
                                  } else {
                                    setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                                  }
                                }}
                                className="w-4 h-4 text-emerald-500 rounded"
                              />
                              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                                {(user.first_name?.[0] || user.display_name?.[0] || user.email?.[0] || "U").toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.display_name || user.email}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{user.email}</p>
                              </div>
                            </label>
                          ))
                        )}
                      </div>

                      {selectedUsers.length > 0 && (
                        <button
                          onClick={handleAddSelectedUsers}
                          disabled={addingUsers}
                          className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                        >
                          {addingUsers ? "Adding..." : `Add ${selectedUsers.length} User(s)`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleShowAddUser}
                      className="w-full py-3 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl text-gray-500 hover:border-emerald-500 hover:text-emerald-500 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 4v16m8-8H4" />
                      </svg>
                      Add Users to Organization
                    </button>
                  )}

                  {/* Users List */}
                  {company?.users?.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400">No users in this organization yet</p>
                    </div>
                  ) : (() => {
                    const q = listUserQuery.trim().toLowerCase();
                    const filtered = q
                      ? (company.users || []).filter((u) => {
                          const full = [u.first_name, u.last_name].filter(Boolean).join(" ").toLowerCase();
                          return (
                            full.includes(q)
                            || (u.email || "").toLowerCase().includes(q)
                            || (u.display_name || "").toLowerCase().includes(q)
                          );
                        })
                      : (company.users || []);
                    return (
                      <div className="space-y-2">
                        {/* Search bar */}
                        <div className="relative">
                          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                          </svg>
                          <input
                            type="search"
                            value={listUserQuery}
                            onChange={(e) => setListUserQuery(e.target.value)}
                            placeholder={`Search ${company.users.length} users by name or email…`}
                            className="w-full pl-9 pr-8 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                          />
                          {listUserQuery && (
                            <button
                              type="button"
                              onClick={() => setListUserQuery("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                              aria-label="Clear search"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          )}
                        </div>

                        {memberRoleMsg && (
                          <p className="text-xs text-red-600 dark:text-red-400 px-1">{memberRoleMsg}</p>
                        )}

                        {q && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                            {filtered.length === 0
                              ? `No matches for "${listUserQuery}"`
                              : `${filtered.length} of ${company.users.length} match`}
                          </p>
                        )}

                        {filtered.length === 0 ? (
                          <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                            No users match this search.
                          </div>
                        ) : (
                          filtered.map((user) => {
                            // Match to the Supabase member row (carries role_id/role_name);
                            // fall back to email in case ids differ between API + Supabase.
                            const memberRow = members.find((m) => m.user_id === user.id)
                              || members.find((m) => (m.email || "").toLowerCase() === (user.email || "").toLowerCase());
                            const showRoleSelect = canEditRoles && memberRow && orgRoles.length > 0;
                            // Epic 3 (2026-08-13) — property reach for this member.
                            const isScoped = memberRow?.role_name === "org_scoped";
                            const totalOrgClients = company?.projects?.length || 0;
                            const reach = (() => {
                              if (!isScoped) return { text: `All properties (${totalOrgClients})`, scoped: false };
                              const r = memberReach[user.id];
                              if (!r || r.loading) return { text: "Loading property reach…", scoped: true, loading: true };
                              if (r.error) return { text: "Scoped (couldn't load details)", scoped: true, error: true };
                              const names = r.names || [];
                              if (names.length === 0) return { text: "0 properties", scoped: true };
                              const list = names.length > 3
                                ? `${names.slice(0, 3).join(", ")} +${names.length - 3} more`
                                : names.join(", ");
                              return { text: `${names.length} ${names.length === 1 ? "property" : "properties"}: ${list}`, scoped: true };
                            })();
                            return (
                            <div
                              key={user.id}
                              className="flex items-start justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                            >
                              {/* Left: avatar + identity + reach badge */}
                              <div className="flex items-start gap-3 min-w-0 flex-1">
                                <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
                                  {(user.first_name?.[0] || user.display_name?.[0] || user.email?.[0] || "U").toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                                    {[user.first_name, user.last_name].filter(Boolean).join(" ") || user.display_name || user.email}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                  {/* Reach badge — green pill for all, amber pill for scoped */}
                                  <div className="mt-1.5 flex items-center gap-1.5">
                                    {reach.scoped ? (
                                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 ${reach.loading || reach.error ? "italic opacity-70" : ""}`}
                                        title="Scoped via per-property grants — sees only the listed properties">
                                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                        {reach.text}
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                        title="Reaches every property through org membership">
                                        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                        {reach.text}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {/* Right: role + actions stacked vertically */}
                              <div className="flex flex-col items-end gap-1.5 shrink-0">
                                {/* Role dropdown / badge */}
                                {showRoleSelect ? (
                                  <select
                                    value={memberRow.role_id || ""}
                                    disabled={!!memberRoleBusy[user.id]}
                                    onChange={(e) => handleMemberRoleChange(user.id, e.target.value)}
                                    className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50 cursor-pointer"
                                    title="Change role in this organization"
                                  >
                                    {!orgRoles.some((r) => r.id === memberRow.role_id) && (
                                      <option value={memberRow.role_id || ""} disabled>
                                        {roleLabel(memberRow.role_name)}
                                      </option>
                                    )}
                                    {orgRoles.map((r) => (
                                      <option key={r.id} value={r.id}>{roleLabel(r.name)}</option>
                                    ))}
                                  </select>
                                ) : memberRow?.role_name ? (
                                  <span className="text-xs px-2.5 py-1 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium">
                                    {roleLabel(memberRow.role_name)}
                                  </span>
                                ) : null}
                                {/* Action buttons row */}
                                <div className="flex items-center gap-1">
                                  {canEditRoles && memberRow && (
                                    <button
                                      onClick={() => openScopeModal(user, memberRow)}
                                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-amber-600 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors font-medium"
                                      title="Choose which properties this member can see"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" /><path d="M8 11h.01M12 11h.01M16 11h.01" /></svg>
                                      {isScoped ? "Edit scope" : "Scope"}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleRemoveUser(user)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
                                    title="Remove from organization"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                            );
                          })
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Projects Tab */}
              {activeTab === "projects" && (
                <div className="space-y-4">
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowCreateProjectForm(true)}
                      className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 4v16m8-8H4" />
                      </svg>
                      Create New Client
                    </button>
                    <button
                      onClick={handleShowAddProject}
                      className="flex-1 py-3 border-2 border-emerald-500 text-emerald-600 dark:text-emerald-400 rounded-xl font-medium hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      Add Existing Client
                    </button>
                  </div>

                  {/* Add Existing Client Panel */}
                  {showAddProjectPanel && (
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add Existing Clients</h3>
                        <button
                          onClick={() => {
                            setShowAddProjectPanel(false);
                            setSelectedProjects([]);
                            setSearchProject("");
                          }}
                          className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <input
                        type="text"
                        placeholder="Search projects..."
                        value={searchProject}
                        onChange={(e) => setSearchProject(e.target.value)}
                        className="w-full px-3 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm"
                      />

                      <div className="max-h-48 overflow-y-auto space-y-2">
                        {filteredAvailableProjects.length === 0 ? (
                          <p className="text-sm text-gray-500 text-center py-4">No available projects to add</p>
                        ) : (
                          filteredAvailableProjects.map(project => (
                            <label
                              key={project.id}
                              className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                selectedProjects.includes(project.id)
                                  ? "bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800"
                                  : "hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedProjects.includes(project.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedProjects([...selectedProjects, project.id]);
                                  } else {
                                    setSelectedProjects(selectedProjects.filter(id => id !== project.id));
                                  }
                                }}
                                className="w-4 h-4 text-emerald-500 rounded"
                              />
                              {project.logoUrl || project.logo ? (
                                <img src={project.logoUrl || project.logo} alt="" className="w-8 h-8 rounded object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded bg-purple-500 flex items-center justify-center text-white text-xs font-bold">
                                  {project.name?.[0]?.toUpperCase() || "P"}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {project.name}
                                </p>
                                <p className="text-xs text-gray-500 truncate">{project.type || "chat"}</p>
                              </div>
                            </label>
                          ))
                        )}
                      </div>

                      {selectedProjects.length > 0 && (
                        <button
                          onClick={handleAddSelectedProjects}
                          disabled={addingProjects}
                          className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium text-sm disabled:opacity-50"
                        >
                          {addingProjects ? "Adding..." : `Add ${selectedProjects.length} Project(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Projects List */}
                  {company?.projects?.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400">No clients in this organization yet</p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Create a new project or add an existing one</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {company?.projects?.map(project => (
                        <div
                          key={project.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <img 
                              src={project.logoUrl || project.logo || "/Rigsby.png"} 
                              alt={project.name} 
                              className="w-10 h-10 rounded-lg object-cover" 
                              onError={(e) => {
                                e.target.src = "/Rigsby.png";
                              }}
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {project.name}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  project.type === "voice" 
                                    ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                    : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                }`}>
                                  {project.type || "chat"}
                                </span>
                                {project.ai_enabled && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                    AI
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveProject(project)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors flex-shrink-0"
                            title="Remove from company"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </>
          )}
        </div>
      </div>

      {/* ─── Epic 4 (2026-08-13) — scope-a-member sub-modal ─────────────────── */}
      {scopeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Scope properties</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {scopeTarget.user.first_name || scopeTarget.user.display_name || scopeTarget.user.email}
                  {" — "}
                  {scopeTarget.memberRow?.role_name === "org_scoped" ? "currently scoped" : "currently sees all properties"}
                </p>
              </div>
              <button
                onClick={closeScopeModal}
                disabled={scopeBusy}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg disabled:opacity-50"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Select the properties this member can see. Selecting all of them restores full access (un-scopes).
              </p>
              {(company?.projects || []).length === 0 ? (
                <p className="text-sm text-gray-500">This organization has no properties.</p>
              ) : (
                <div className="space-y-1.5">
                  {(company?.projects || []).map((c) => {
                    const id = String(c.id);
                    return (
                      <label key={id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!scopeSelection[id]}
                          disabled={scopeBusy}
                          onChange={(e) => setScopeSelection((s) => ({ ...s, [id]: e.target.checked }))}
                          className="w-4 h-4 rounded border-gray-300 text-emerald-500 focus:ring-emerald-400"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{c.name || id}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {scopeError && <p className="text-xs text-red-600 dark:text-red-400 mt-3">{scopeError}</p>}
            </div>

            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
              <button
                onClick={closeScopeModal}
                disabled={scopeBusy}
                className="text-sm px-3 py-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleScopeSave}
                disabled={scopeBusy || (company?.projects || []).length === 0}
                className="text-sm px-4 py-1.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                {scopeBusy ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyDetailModal;
